/**
 * Reaksiya kutubxonasi sxemasi (packs/<id>/reactions.json).
 *
 * Reaksiya — ma'lumot: stexiometriya, kinetika (Arrhenius), muvozanat (Kp(T) van't Hoff),
 * reaksiya issiqligi, katalizator faolligi va uning pasayishi. Reaktor modellari shu
 * yerdan o'qiydi; ichida reaksiya hardcode qilinmaydi.
 *
 * BIRLIKLAR: ΔH_r — J/mol (1 mol reaksiya yurishi, ya'ni ξ birligi uchun; manfiy — ekzotermik);
 * k0 — (mol/m³)^(1−Σn) / s (konsentratsiya asosida); Ea — J/mol; Kp — bar asosida (p° = 1 bar).
 */
import { z } from 'zod';
import { I18nText } from '../scenario/schema';

export const ReactionSchema = z
  .object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/),
    name: I18nText.optional(),
    /** Tenglama matni (hujjat uchun), masalan "N2 + 3H2 ⇌ 2NH3" */
    equation: z.string().optional(),
    phase: z.enum(['gas', 'liquid']),
    /** komponent → koeffitsient (reagent manfiy, mahsulot musbat) */
    stoich: z.record(z.number()),
    /** kalit reagent (konversiya asosi) */
    key: z.string(),
    /** J/mol (ξ birligi uchun), Tref = 298.15 K; haroratga bog'liq emas deb olinadi */
    dHr: z.number(),
    kinetics: z
      .object({
        type: z.literal('arrhenius'),
        /** oldinga reaksiya tezlik konstantasi pre-eksponenti */
        k0: z.number().positive(),
        /** aktivlanish energiyasi, J/mol */
        Ea: z.number().min(0),
        /** reagentlar tartiblari (bo'lmasa |ν_i| — elementar) */
        orders: z.record(z.number().min(0)).optional(),
      })
      .strict(),
    /** Qaytar reaksiya: Kp(T0) (gaz, bar) yoki Kc(T0) (suyuqlik, mol/m³); van't Hoff bo'yicha ekstrapolyatsiya */
    equilibrium: z
      .object({
        Kp0: z.number().positive().optional(),
        Kc0: z.number().positive().optional(),
        T0: z.number().positive(),
      })
      .strict()
      .optional(),
    catalyst: z
      .object({
        id: z.string(),
        /** boshlang'ich faollik (1 = yangi) */
        activity: z.number().min(0).max(1).default(1),
        /** faollik pasayishi: a(t) = a0·exp(−kd·t) yoki a0·(1 − kd·t) */
        deactivation: z
          .object({ type: z.enum(['exponential', 'linear']), kd: z.number().min(0) })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    /** manba / ekspert izohi */
    source: z.string().optional(),
    /** "o'quv taxmini" bo'lsa true — ekspert tekshiruvi kerak */
    provisional: z.boolean().default(false),
  })
  .strict();
export type ReactionDef = z.infer<typeof ReactionSchema>;

export function parseReaction(json: unknown): ReactionDef {
  const r = ReactionSchema.parse(json);
  const nuKey = r.stoich[r.key];
  if (nuKey === undefined || nuKey >= 0) throw new Error(`reaction ${r.id}: key ${r.key} must be a reactant (negative stoich)`);
  if (r.equilibrium) {
    if (r.phase === 'gas' && r.equilibrium.Kp0 === undefined) throw new Error(`reaction ${r.id}: gas-phase equilibrium needs Kp0`);
    if (r.phase === 'liquid' && r.equilibrium.Kc0 === undefined) throw new Error(`reaction ${r.id}: liquid-phase equilibrium needs Kc0`);
  }
  return r;
}
